import { useState } from "react";
import { Card } from "@/components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { AlertTriangle, Info, CheckCircle2 } from "lucide-react";

const BAND_STYLE = {
  value_verified: { ring: "border-gold/50 bg-gold/10", text: "text-[#8a6d12]" },
  high_confidence: { ring: "border-[#1E9E6A]/30 bg-[#1E9E6A]/8", text: "text-[#1E9E6A]" },
  moderate_confidence: { ring: "border-aqua/50 bg-aqua/15", text: "text-[#1d6e72]" },
  directional: { ring: "border-editorial/30 bg-editorial/8", text: "text-editorial" },
  estimate_only: { ring: "border-coral/40 bg-coral/10", text: "text-coral" },
  no_evidence: { ring: "border-slate-200 bg-slate-50", text: "text-slate-500" },
};

const EQ_PLAIN = "Evidence Quality shows how reliable the AI value claim is based on the information available.";
const EQ_CONTEXT = "NetLift does not treat every AI value claim equally. Evidence Quality helps separate stronger evidence from estimates or incomplete data, so teams can understand how much confidence to place in each result.";

// Qualitative factor status (no numbers, no formula) derived from the backend signals.
function factorStatus(eq) {
  const cov = eq.baseline_coverage ?? 0;
  const timeCmp = cov >= 70 ? "Strong" : cov > 0 ? "Partial" : "Limited";
  const hasMissingCost = (eq.warnings || []).some((w) => w.type === "missing_cost");
  const cost = hasMissingCost ? "Incomplete" : "Complete";
  const source = eq.primary_source
    ? eq.primary_source.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "Not set";
  return [
    { label: "Time comparison", value: timeCmp, good: cov > 0 },
    { label: "Cost data", value: cost, good: !hasMissingCost },
    { label: "Evidence source", value: source, good: !!eq.primary_source },
  ];
}

export function EvidenceQualityCard({ eq, dimension, groupId, testid = "evidence-quality-card" }) {
  const [open, setOpen] = useState(false);
  if (!eq || eq.score == null) {
    return (
      <Card title="Evidence Quality" subtitle={EQ_PLAIN} testid={testid}>
        <p className="text-sm text-slate-400">No evidence recorded yet. Add a time-without-AI comparison and costs to AI-assisted work to build Evidence Quality.</p>
      </Card>
    );
  }
  const style = BAND_STYLE[eq.band] || BAND_STYLE.no_evidence;
  const factors = factorStatus(eq);
  return (
    <Card
      title="Evidence Quality"
      subtitle={EQ_PLAIN}
      testid={testid}
      actions={<Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="evidence-drilldown-btn"><Info className="mr-1.5 h-3.5 w-3.5" /> Why this rating</Button>}
    >
      <div className={`flex items-center justify-between rounded-lg border px-4 py-3 ${style.ring}`} data-testid="evidence-band">
        <div>
          <p className={`text-sm font-semibold uppercase tracking-[0.14em] ${style.text}`} data-testid="evidence-band-label">{eq.band_label}</p>
          <p className="mt-1 text-xs text-slate-500">Based on {eq.sample_size} AI-assisted {eq.sample_size === 1 ? "task" : "tasks"}</p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2">
        {factors.map((f) => (
          <div key={f.label} className="rounded-lg border border-ink/10 bg-clean/30 px-3 py-2" data-testid={`evidence-factor-${f.label.toLowerCase().replace(/\s/g, "-")}`}>
            <p className="text-[10px] uppercase tracking-widest text-slate-400">{f.label}</p>
            <p className={`mt-0.5 text-sm font-medium ${f.good ? "text-ink" : "text-coral"}`}>{f.value}</p>
          </div>
        ))}
      </div>

      {eq.warnings?.length > 0 && (
        <div className="mt-4 space-y-1.5">
          {eq.warnings.map((w) => (
            <p key={w.type} className="flex items-start gap-2 rounded-md bg-coral/8 px-3 py-2 text-xs text-coral" data-testid={`evidence-warning-${w.type}`}>
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {w.message}
            </p>
          ))}
        </div>
      )}

      <EvidenceWhyDialog open={open} onOpenChange={setOpen} dimension={dimension} groupId={groupId} fallback={eq} />
    </Card>
  );
}

function EvidenceWhyDialog({ open, onOpenChange, dimension, groupId, fallback }) {
  const [data, setData] = useState(null);
  const load = () => {
    const params = new URLSearchParams();
    if (dimension && groupId) { params.set("dimension", dimension); params.set("group_id", groupId); }
    api.get(`/analytics/evidence-quality?${params.toString()}`).then((r) => setData(r.data)).catch(() => setData(null));
  };
  const eq = data?.evidence_quality || fallback;
  const factors = eq ? factorStatus(eq) : [];
  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (v) load(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Why this Evidence Quality rating</DialogTitle>
          <DialogDescription>{EQ_PLAIN}</DialogDescription>
        </DialogHeader>
        <div className="space-y-5" data-testid="evidence-why">
          <p className="text-sm leading-relaxed text-slate-600">{EQ_CONTEXT}</p>

          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">What NetLift looked at</p>
            <div className="space-y-2">
              {factors.map((f) => (
                <div key={f.label} className="flex items-center justify-between rounded-md border border-ink/10 px-3 py-2 text-sm">
                  <span className="text-slate-600">{f.label}</span>
                  <span className={`inline-flex items-center gap-1.5 font-medium ${f.good ? "text-[#1E9E6A]" : "text-coral"}`}>
                    {f.good ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />} {f.value}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {eq?.warnings?.length > 0 && (
            <div className="space-y-1.5">
              {eq.warnings.map((w) => (
                <p key={w.type} className="flex items-start gap-2 rounded-md bg-coral/8 px-3 py-2 text-xs text-coral">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {w.message}
                </p>
              ))}
            </div>
          )}

          {data?.records?.length > 0 && (
            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Contributing records ({data.record_count})</p>
              <div className="max-h-44 overflow-auto rounded-md border border-ink/10">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-white"><tr className="text-left text-slate-400">
                    <th className="px-3 py-1.5 font-medium">Work</th>
                    <th className="px-3 py-1.5 font-medium">Date</th>
                    <th className="px-3 py-1.5 font-medium">Evidence source</th>
                    <th className="px-3 py-1.5 font-medium text-right">Time without AI</th>
                  </tr></thead>
                  <tbody>
                    {data.records.slice(0, 50).map((r) => (
                      <tr key={r.id} className="border-t border-slate-50">
                        <td className="px-3 py-1.5 text-slate-700">{r.title || "Untitled"}</td>
                        <td className="px-3 py-1.5 text-slate-500">{r.entry_date}</td>
                        <td className="px-3 py-1.5 text-slate-500">{r.baseline_source}{!r.has_baseline && " (none)"}</td>
                        <td className="px-3 py-1.5 text-right text-slate-600">{r.has_baseline ? `${r.baseline_hours}h` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
